import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, ShieldCheck, FileText, FileCode, Download, Server, CheckCircle2, Globe,
} from "lucide-react";
import api from "lib/api";

// ---- helpers de apresentação ---------------------------------------------

function fmtDate(d) {
  return d ? String(d).replace("T", " ").slice(0, 19) : "—";
}

function humanDuration(notBefore, notAfter, days) {
  if (Number.isFinite(days)) return `${days} dias`;
  if (!notBefore || !notAfter) return null;
  const d = Math.round((new Date(notAfter) - new Date(notBefore)) / 86400000);
  return Number.isFinite(d) ? `${d} dias` : null;
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h2 className="mb-2 border-b border-border pb-1 text-sm font-bold uppercase tracking-wide text-primary">
        {title}
      </h2>
      <div className="divide-y divide-border/40">{children}</div>
    </div>
  );
}

function Row({ label, children, mono }) {
  // sem valor -> não exibe a linha.
  if (children === null || children === undefined || children === false || children === "") {
    return null;
  }
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-3 py-1.5 text-sm">
      <div className="text-right font-medium text-muted-foreground">{label}</div>
      <div className={"min-w-0 break-all " + (mono ? "font-mono text-xs" : "")}>{children}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 text-sm font-bold text-primary">{title}</div>
      {children}
    </div>
  );
}

function SideRow({ label, children }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2 py-1 text-sm">
      <div className="text-right font-medium text-muted-foreground">{label}</div>
      <div className="min-w-0 break-words">{children || <span className="italic text-muted-foreground/60">—</span>}</div>
    </div>
  );
}

function Expandable({ value, head = 96 }) {
  const [open, setOpen] = useState(false);
  if (!value) return <span className="italic text-muted-foreground/60">—</span>;
  const long = value.length > head;
  return (
    <span className="font-mono text-xs">
      {open || !long ? value : value.slice(0, head) + "…"}
      {long && (
        <button onClick={() => setOpen(!open)} className="ml-1 text-primary hover:underline">
          {open ? "menos" : "mais"}
        </button>
      )}
    </span>
  );
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors " +
        (active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {Icon && <Icon className="h-4 w-4" />} {children}
    </button>
  );
}

// ---- página ---------------------------------------------------------------

export default function CertDetail() {
  const { thumbprint } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("certificate");

  useEffect(() => {
    api
      .get(`/cert/${thumbprint}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Falha ao carregar certificado."));
  }, [thumbprint]);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  const p = data.parsed || null;
  const ext = p?.extensions || {};
  const pk = p?.public_key || null;

  const subjectDn = p?.subject_dn || data.subject;
  const issuerDn = p?.issuer_dn || data.issuer;
  const sanDns = ext.san?.dns || (data.san || []).filter((s) => !/^\d+\.\d+\.\d+\.\d+$/.test(s));
  const sanIp = ext.san?.ip || (data.san || []).filter((s) => /^\d+\.\d+\.\d+\.\d+$/.test(s));
  const allNames = [...sanDns, ...sanIp];

  const expired = data.not_after && new Date(data.not_after) < new Date();
  const labels = [
    data.is_root_ca && "root-ca",
    data.is_ca && !data.is_root_ca && "ca",
    data.self_signed && "self-signed",
    !data.is_ca && "leaf",
    expired ? "expired" : "unexpired",
  ].filter(Boolean);

  function downloadPem() {
    const blob = new Blob([data.pem], { type: "application/x-pem-file" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(data.fingerprint || "cert").slice(0, 16)}.pem`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const keyTypeLabel = pk
    ? pk.type === "RSA"
      ? `${pk.bits}-bit RSA, e = ${pk.exponent}`
      : pk.type === "EC"
      ? `${pk.bits}-bit EC (${pk.curve})`
      : pk.type
    : null;

  return (
    <div className="w-full space-y-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" /> Resultados
      </button>

      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-amber-400/80" />
        <h1 className="text-2xl font-semibold">{data.title}</h1>
      </div>

      {/* Sub-abas */}
      <div className="flex gap-1 border-b border-border">
        <TabButton active={tab === "certificate"} onClick={() => setTab("certificate")} icon={ShieldCheck}>
          Certificado
        </TabButton>
        {data.pem && (
          <TabButton active={tab === "pem"} onClick={() => setTab("pem")} icon={Download}>
            PEM
          </TabButton>
        )}
        {data.raw_text && (
          <TabButton active={tab === "raw"} onClick={() => setTab("raw")} icon={FileCode}>
            Raw
          </TabButton>
        )}
      </div>

      {tab === "certificate" && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Coluna principal */}
          <div className="lg:col-span-2">
            <Section title="Informações básicas">
              <Row label="Subject DN" mono>{subjectDn}</Row>
              <Row label="Issuer DN" mono>{issuerDn}</Row>
              {p && (
                <Row label="Serial Number" mono>
                  <div>Decimal: {p.serial_dec}</div>
                  <div>Hex: {p.serial_hex}</div>
                </Row>
              )}
              <Row label="Validade">
                {fmtDate(data.not_before)} <span className="text-muted-foreground">→</span> {fmtDate(data.not_after)}
                {humanDuration(data.not_before, data.not_after, p?.validity_days) && (
                  <span className="text-muted-foreground"> ({humanDuration(data.not_before, data.not_after, p?.validity_days)})</span>
                )}
              </Row>
              <Row label="All Names">
                {allNames.length ? (
                  <div className="flex flex-col">
                    {allNames.map((n, i) => <span key={i} className="font-mono text-xs">{n}</span>)}
                  </div>
                ) : null}
              </Row>
              <Row label="Labels">
                <div className="flex flex-wrap gap-1.5">
                  {labels.map((l) => (
                    <span key={l} className="rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs">{l}</span>
                  ))}
                </div>
              </Row>
            </Section>

            <Section title="Fingerprint">
              <Row label="SHA-256" mono>{p?.fingerprints?.sha256 || data.fingerprint}</Row>
              {p?.fingerprints?.sha1 && <Row label="SHA-1" mono>{p.fingerprints.sha1}</Row>}
              {p?.fingerprints?.md5 && <Row label="MD5" mono>{p.fingerprints.md5}</Row>}
            </Section>

            {pk && (
              <Section title="Chave pública">
                <Row label="Key Type">
                  {keyTypeLabel}
                  {pk.strong && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" /> forte
                    </span>
                  )}
                </Row>
                {pk.modulus && <Row label="Modulus"><Expandable value={pk.modulus} /></Row>}
                {pk.spki_sha256 && <Row label="SPKI SHA-256" mono>{pk.spki_sha256}</Row>}
              </Section>
            )}

            {p && (
              <Section title="Assinatura">
                <Row label="Algorithm">
                  {p.signature_algorithm}
                  {p.signature_oid && <span className="text-muted-foreground"> ({p.signature_oid})</span>}
                </Row>
              </Section>
            )}

            {p && (
              <Section title="Extensões">
                {ext.authority_key_id && <Row label="Auth Key ID" mono>{ext.authority_key_id}</Row>}
                {ext.subject_key_id && <Row label="Subject Key ID" mono>{ext.subject_key_id}</Row>}
                {ext.key_usage?.length > 0 && <Row label="Key Usage">{ext.key_usage.join(", ")}</Row>}
                {ext.ext_key_usage?.length > 0 && <Row label="Ext. Key Usage">{ext.ext_key_usage.join(", ")}</Row>}
                {ext.crl?.length > 0 && (
                  <Row label="CRL Paths">
                    {ext.crl.map((u, i) => <div key={i}><a href={u} className="text-primary hover:underline" target="_blank" rel="noreferrer">{u}</a></div>)}
                  </Row>
                )}
                {ext.aia?.ocsp?.length > 0 && (
                  <Row label="OCSP">
                    {ext.aia.ocsp.map((u, i) => <div key={i}><a href={u} className="text-primary hover:underline" target="_blank" rel="noreferrer">{u}</a></div>)}
                  </Row>
                )}
                {ext.aia?.ca_issuers?.length > 0 && (
                  <Row label="CA Issuers">
                    {ext.aia.ca_issuers.map((u, i) => <div key={i}><a href={u} className="text-primary hover:underline" target="_blank" rel="noreferrer">{u}</a></div>)}
                  </Row>
                )}
                {ext.policies?.length > 0 && <Row label="Policies">{ext.policies.join(", ")}</Row>}
                {ext.basic_constraints && (
                  <Row label="Constraints">
                    Is CA: {ext.basic_constraints.ca ? "True" : "False"}
                    {ext.basic_constraints.path_length != null && `, pathlen: ${ext.basic_constraints.path_length}`}
                  </Row>
                )}
              </Section>
            )}
          </div>

          {/* Coluna lateral */}
          <div className="space-y-4">
            <Card title="Uso da chave e restrições">
              <SideRow label="É CA?">{data.is_ca ? "Sim" : "Não"}</SideRow>
              {data.is_ca && <SideRow label="Root CA?">{data.is_root_ca ? "Sim" : "Não"}</SideRow>}
              <SideRow label="Auto-assinado">{data.self_signed ? "Sim" : "Não"}</SideRow>
              {ext.key_usage?.length > 0 && <SideRow label="Key Usage">{ext.key_usage.join(", ")}</SideRow>}
              {ext.ext_key_usage?.length > 0 && <SideRow label="Ext. Usage">{ext.ext_key_usage.join(", ")}</SideRow>}
            </Card>

            <Card title={`Hosts (${data.host_count || 0})`}>
              {data.hosts?.length ? (
                <div className="flex flex-col gap-1">
                  {data.hosts.map((ip) => (
                    <Link key={ip} to={`/host/${ip}`} className="flex items-center gap-1.5 font-mono text-sm text-primary hover:underline">
                      <Server className="h-3.5 w-3.5" /> {ip}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Nenhum host observado.</div>
              )}
            </Card>

            <Card title={`Domínios (${data.domains?.length || 0})`}>
              {data.domains?.length ? (
                <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                  {data.domains.map((d) => (
                    <Link key={d} to={`/domain/${d}`} className="flex items-center gap-1.5 truncate font-mono text-sm text-primary hover:underline">
                      <Globe className="h-3.5 w-3.5 shrink-0" /> {d}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Nenhum domínio (SOA) encontrado.</div>
              )}
            </Card>

            <Card title={`Hostnames (${data.hostnames?.length || 0})`}>
              {data.hostnames?.length ? (
                <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                  {data.hostnames.map((h) => (
                    <span key={h} className="truncate font-mono text-xs text-muted-foreground">{h}</span>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Nenhum hostname.</div>
              )}
            </Card>
          </div>
        </div>
      )}

      {tab === "pem" && data.pem && (
        <div className="space-y-3">
          <button
            onClick={downloadPem}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Download className="h-4 w-4" /> Baixar PEM
          </button>
          <pre className="overflow-x-auto rounded-lg border border-border bg-card p-4 font-mono text-xs leading-relaxed">{data.pem}</pre>
        </div>
      )}

      {tab === "raw" && data.raw_text && (
        <div className="flex items-start gap-2">
          <FileText className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
          <pre className="flex-1 overflow-x-auto rounded-lg border border-border bg-card p-4 font-mono text-xs leading-relaxed">{data.raw_text}</pre>
        </div>
      )}
    </div>
  );
}
